import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { PageHeader } from '@/components/admin/AdminShell';
import { ConfirmDialog } from '@/components/admin/ConfirmDialog';
import { MetaBox } from '@/components/admin/MetaBox';
import { Notice } from '@/components/admin/Notice';
import { ErrorState, ForbiddenState, SkeletonRows } from '@/components/admin/states';
import {
  MEMBER_STATUS_LABELS,
  changeMemberStatus,
  deleteMemberPhoto,
  fetchMember,
  updateMember,
  uploadMemberPhoto,
  type MemberStatus,
} from '@/features/members/api';
import { MemberFormFields, type MemberFormValues } from '@/features/members/MemberFormFields';
import { MemberPhotoField } from '@/features/members/MemberPhotoField';
import { MemberRelatedNav } from '@/features/members/MemberRelatedNav';
import { fetchCohorts, fetchCourses } from '@/features/masters/api';
import { ApiError } from '@/lib/api/http';

const STATUS_OPTIONS: MemberStatus[] = ['active', 'graduated', 'withdrawn', 'expelled'];

/**
 * 会員のプロフィール編集（事務局のみ / D-142・D-143）。
 * 基本情報・顔写真・在籍状態の変更をここにまとめる。Meet用メール・通知の受け取り方・
 * ログイン情報は「連携設定編集」（`MemberIntegrationsEditPage`）に分けた（D-143。
 * ユーザー指摘「プロフィール編集（あくまでもプロフィルとステータス）／連携設定編集…で
 * リンク分けたほうが良いのでは？」）。会員詳細（/members/:id）はタブ中心の概要のみ。
 */
export function MemberEditPage(): ReactElement {
  const { memberId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['member', memberId],
    queryFn: () => fetchMember(memberId),
    enabled: Boolean(memberId),
  });
  const cohorts = useQuery({ queryKey: ['cohorts'], queryFn: fetchCohorts });
  const courses = useQuery({ queryKey: ['courses'], queryFn: fetchCourses });

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [formValues, setFormValues] = useState<MemberFormValues | null>(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<MemberStatus | null>(null);

  if (query.isPending) return <SkeletonRows rows={6} />;
  if (query.isError) {
    const forbidden = query.error instanceof ApiError && query.error.status === 403;
    return forbidden ? (
      <ForbiddenState />
    ) : (
      <ErrorState message={query.error.message} onRetry={() => void query.refetch()} />
    );
  }

  const member = query.data;
  const values: MemberFormValues = formValues ?? {
    member_id: member.member_id,
    invite_email: '',
    name: member.name,
    name_kana: member.name_kana ?? '',
    company: member.company ?? '',
    position: member.position ?? '',
    specialty: member.specialty ?? '',
    cohort_id: member.cohort_id ?? '',
    course_id: member.course_id ?? '',
    bio: member.bio ?? '',
    company_url: member.company_url ?? '',
    subordinate_count: member.subordinate_count === null ? '' : String(member.subordinate_count),
    years_of_service: member.years_of_service === null ? '' : String(member.years_of_service),
    hobbies: member.hobbies ?? '',
    career_history: member.career_history ?? '',
    participation_purpose: member.participation_purpose ?? '',
    strengths: member.strengths ?? '',
    weaknesses: member.weaknesses ?? '',
    goals: member.goals ?? '',
    phone: member.phone ?? '',
    contact_email: member.contact_email ?? '',
    tags: member.tags.join(', '),
  };

  async function onSave(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await updateMember(member.id, {
        name: values.name,
        name_kana: values.name_kana || null,
        company: values.company || null,
        position: values.position || null,
        specialty: values.specialty || null,
        cohort_id: values.cohort_id,
        course_id: values.course_id,
        bio: values.bio || null,
        company_url: values.company_url || null,
        subordinate_count:
          values.subordinate_count === '' ? null : Number(values.subordinate_count),
        years_of_service: values.years_of_service === '' ? null : Number(values.years_of_service),
        hobbies: values.hobbies || null,
        career_history: values.career_history || null,
        participation_purpose: values.participation_purpose || null,
        strengths: values.strengths || null,
        weaknesses: values.weaknesses || null,
        goals: values.goals || null,
        phone: values.phone || null,
        contact_email: values.contact_email || null,
        tags: values.tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
      });
      await queryClient.invalidateQueries({ queryKey: ['member', memberId] });
      navigate(`/members/${member.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '更新に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onConfirmStatusChange(reason: string): Promise<void> {
    if (!pendingStatus) return;
    setError(null);
    try {
      await changeMemberStatus(member.id, pendingStatus, reason);
      await queryClient.invalidateQueries({ queryKey: ['member', memberId] });
      setStatusDialogOpen(false);
      setPendingStatus(null);
      setNotice('在籍状態を更新しました。');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '在籍状態の変更に失敗しました。');
    }
  }

  return (
    <>
      <PageHeader
        title={`${member.name}さんのプロフィール編集`}
        description={`会員ID: ${member.member_id}`}
      />
      <MemberRelatedNav memberId={member.id} current="edit" />

      {notice && (
        <Notice kind="success" onDismiss={() => setNotice(null)}>
          {notice}
        </Notice>
      )}
      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}

      <MetaBox title="基本情報">
        <div className="mb-4">
          <span className="mb-1.5 block text-wptext-muted">顔写真</span>
          <MemberPhotoField
            photoUrl={member.photo_url}
            name={member.name}
            editable
            onUpload={async (file) => {
              await uploadMemberPhoto(member.id, file);
              await queryClient.invalidateQueries({ queryKey: ['member', memberId] });
              setNotice('顔写真を更新しました。');
            }}
            onDelete={async () => {
              await deleteMemberPhoto(member.id);
              await queryClient.invalidateQueries({ queryKey: ['member', memberId] });
              setNotice('顔写真を削除しました。');
            }}
          />
        </div>

        <MemberFormFields
          values={values}
          onChange={setFormValues}
          cohorts={cohorts.data ?? []}
          courses={courses.data ?? []}
          memberIdEditable={false}
        />
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            disabled={busy || !values.name || !values.cohort_id || !values.course_id}
            onClick={() => void onSave()}
            className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
          >
            保存
          </button>
          <button
            type="button"
            onClick={() => navigate(`/members/${member.id}`)}
            className="btn-secondary"
          >
            キャンセル
          </button>
        </div>
      </MetaBox>

      <div className="mt-4">
        <MetaBox title="在籍状態の変更">
          <p className="mb-3 text-wptext-muted">
            現在のステータス:{' '}
            <span className="font-semibold text-wptext-strong">
              {MEMBER_STATUS_LABELS[member.status]}
            </span>
          </p>
          <div className="flex flex-wrap gap-2">
            {STATUS_OPTIONS.filter((s) => s !== member.status).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => {
                  setPendingStatus(s);
                  setStatusDialogOpen(true);
                }}
                className="btn-secondary px-2 py-1"
              >
                → {MEMBER_STATUS_LABELS[s]}に変更
              </button>
            ))}
          </div>
        </MetaBox>
      </div>

      <ConfirmDialog
        open={statusDialogOpen}
        title={
          pendingStatus
            ? `在籍状態を「${MEMBER_STATUS_LABELS[pendingStatus]}」に変更しますか？`
            : '在籍状態を変更しますか？'
        }
        requireReason
        confirmLabel="変更する"
        onConfirm={(reason) => void onConfirmStatusChange(reason)}
        onCancel={() => {
          setStatusDialogOpen(false);
          setPendingStatus(null);
        }}
      />

      <button
        type="button"
        onClick={() => navigate(`/members/${member.id}`)}
        className="mt-4 text-wpaccent hover:underline"
      >
        ← 会員詳細に戻る
      </button>
    </>
  );
}
